/**
 * Автоподхват новой сборки (A27).
 *
 * У веб-сборки скаута нет Service Worker, а вкладка на телефоне живёт днями:
 * после выката старая вкладка продолжает работать со старым кодом, пока
 * скаут сам не перезагрузит её — а он не знает, что надо. Две защиты:
 *
 * 1. `vite:preloadError` — ленивый чанк (карта, сканер) исчез после выката:
 *    имена файлов хешированы, старых на сервере больше нет. Одна
 *    перезагрузка перечитывает `index.html` с живыми именами.
 * 2. Возврат на вкладку и раз в 5 минут — свежий `index.html` (no-store):
 *    главный бандл сменился — перезагрузка, но не посреди действия.
 *
 * ⚠ ПЕРЕЗАГРУЗКА ТОЛЬКО ПОСЛЕ УСПЕШНОГО ОТВЕТА. Без сети `index.html` не
 * придёт, и это не повод перезагружаться: без SW перезагрузка офлайн убила
 * бы вкладку вместе с офлайн-работой.
 *
 * Обе защиты не зацикливаются — отметки в sessionStorage (на вкладку, не на
 * браузер: метка, пережившая вкладку, отключила бы защиту при следующем
 * выкате). Повторный провал чанка внутри минуты уходит обычной ошибкой, а не
 * новой перезагрузкой; ради одной и той же сборки перезагружаемся один раз,
 * даже если кэш по дороге всё ещё отдаёт старую.
 */

export const PRELOAD_MARK = 'scout.preload-reload-at'
export const RELOAD_FOR = 'scout.reload-for'
export const PRELOAD_COOLDOWN_MS = 60_000
export const CHECK_EVERY_MS = 5 * 60_000
/** Как часто проверять, освободился ли скаут, когда новая сборка уже найдена. */
export const BUSY_RETRY_MS = 10_000

/** Главный бандл страницы: `assets/index-<хеш>.js` у модульного скрипта. */
export function mainBundle(html: string): string | null {
  for (const tag of html.match(/<script\b[^>]*>/g) ?? []) {
    if (!/\btype=["']module["']/.test(tag)) continue
    const src = /\bsrc=["']([^"']+)["']/.exec(tag)?.[1] ?? ''
    const m = /assets\/[^/]+\.js$/.exec(src)
    if (m) return m[0]
  }
  return null
}

/**
 * Скаут посреди действия — перезагрузка подождёт: открыт нижний лист или
 * всплывашка байка, работает камера сканера, в поле что-то набрано.
 * Пустое поле с фокусом — не действие: на главном экране оно в фокусе всегда.
 */
export function pageBusy(doc: Document): boolean {
  if (doc.querySelector('[role="dialog"], .mapboxgl-popup, video')) return true
  const el = doc.activeElement
  if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return false
  if (el instanceof HTMLInputElement && ['checkbox', 'radio', 'button', 'submit'].includes(el.type)) {
    return false
  }
  return el.value !== ''
}

type Deps = {
  win: Window
  storage: Pick<Storage, 'getItem' | 'setItem'>
  reload: () => void
  now: () => number
}

const defaults = (): Deps => ({
  win: window,
  storage: sessionStorage,
  reload: () => {
    window.location.reload()
  },
  now: () => Date.now(),
})

function read(storage: Deps['storage'], key: string): string | null {
  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

function write(storage: Deps['storage'], key: string, value: string): void {
  try {
    storage.setItem(key, value)
  } catch {
    /* приватный режим — защита от петли держится на памяти вкладки */
  }
}

/** Исчезнувший после выката чанк — одна перезагрузка. Возвращает отписку. */
export function installPreloadReload(over: Partial<Deps> = {}): () => void {
  const { win, storage, reload, now } = { ...defaults(), ...over }
  let reloading = false
  const onError = (e: Event) => {
    if (reloading) {
      e.preventDefault()
      return
    }
    const last = Number(read(storage, PRELOAD_MARK) ?? 0)
    // Второй провал подряд — не выкат, а настоящая ошибка: пусть её увидят.
    if (now() - last < PRELOAD_COOLDOWN_MS) return
    write(storage, PRELOAD_MARK, String(now()))
    reloading = true
    e.preventDefault()
    reload()
  }
  win.addEventListener('vite:preloadError', onError)
  return () => {
    win.removeEventListener('vite:preloadError', onError)
  }
}

/**
 * Следить за выкатом: на возврате во вкладку и раз в `CHECK_EVERY_MS`.
 * Возвращает остановку. Без своего бандла (dev-сервер Vite) — ничего не делает.
 */
export function watchForNewBuild(
  opts: {
    isBusy: () => boolean
    current?: string | null
    fetchHtml?: () => Promise<string>
    doc?: Document
  } & Partial<Deps>,
): () => void {
  const { win, storage, reload } = { ...defaults(), ...opts }
  const doc = opts.doc ?? document
  const current = opts.current !== undefined ? opts.current : mainBundle(doc.documentElement.outerHTML)
  const fetchHtml =
    opts.fetchHtml ??
    (async () => {
      const res = await fetch('./index.html', { cache: 'no-store' })
      if (!res.ok) throw new Error(`index.html: ${res.status}`)
      return res.text()
    })
  if (!current) return () => {}

  let pending: string | null = null
  let checking = false

  const tryReload = () => {
    if (pending === null || opts.isBusy()) return
    // Уже перезагружались ради этой сборки и всё равно на старой — кэш по
    // дороге отдаёт старый index.html; вторая перезагрузка ничего не даст.
    if (read(storage, RELOAD_FOR) === pending) {
      pending = null
      return
    }
    write(storage, RELOAD_FOR, pending)
    reload()
  }

  const check = async () => {
    if (checking) return
    checking = true
    try {
      const fresh = mainBundle(await fetchHtml())
      if (fresh && fresh !== current) {
        pending = fresh
        tryReload()
      }
    } catch {
      /* нет сети или сервер не ответил — не повод перезагружаться */
    } finally {
      checking = false
    }
  }

  const onVisibility = () => {
    if (doc.visibilityState === 'visible') void check()
  }
  doc.addEventListener('visibilitychange', onVisibility)
  const every = win.setInterval(onVisibility, CHECK_EVERY_MS)
  const retry = win.setInterval(tryReload, BUSY_RETRY_MS)
  return () => {
    doc.removeEventListener('visibilitychange', onVisibility)
    win.clearInterval(every)
    win.clearInterval(retry)
  }
}
